import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DocumentationValidationStatusEnum,
  DocumentationVersionReadStatusEnum,
  FormAnswer,
  FormAnswerResp,
  FormTemplateQuestionFieldOption,
} from 'graphql-common';
import { BaseItem, CellProps } from '@lib/components/Table/types';
import TextCell from '@lib/components/Table/commonComponents/TextCell/TextCell';
import CollapseCell from '@lib/components/Table/commonComponents/CollapseCell/CollapseCell';
import { CollapseCropColor } from '@lib/components/Collapse/Collapse';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import DocumentationPreview from '@lib/components/DocumentationPreview/DocumentationPreview';
import { DocumentationPreviewEnum } from '@lib/components/DocumentationPreview/enums';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import SupportiveMessage, {
  SupportiveMessageColor,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import Typography from '@lib/components/Typography/Typography';
import useDocumentsMap from '@lib/utils/fetch/hooks/useDocumentsMap';
import formatReadingDuration from '@lib/utils/formatReadingDuration';
import styles from './DocumentResponseCell.module.scss';

type FormAnswerRespWithReadStatus = FormAnswerResp & {
  status?: DocumentationVersionReadStatusEnum | null;
  documentationVersionReadId?: string | null;
};

export interface DocumentResponseCellRow extends BaseItem {
  options?: FormTemplateQuestionFieldOption[] | null;
  answer?: Pick<FormAnswer, 'resp' | 'documentationVersionReads'> | null;
}

type Props<T extends DocumentResponseCellRow> = CellProps<T> & {
  // Cross-answer fallback: built once at the table/page level by walking ALL
  // answers' `documentationVersionReads` and indexing by `documentationId`.
  // Used to recover reading time on `READ_PREVIOUSLY` rows whose DVR was
  // attached to a sibling answer in the same submission (the per-resp
  // `documentationVersionReadId` is intentionally null on those rows).
  readingDurationByDocumentationId?: Map<string, number>;
  onDocumentClick?: (documentationId: string) => void;
  // EDM-locked render: when the company plan strips optionDocumentation
  // (BE returns null for every option), show an upgrade affordance to Full
  // Admins and a neutral "unavailable" SupportiveMessage to everyone else.
  isViewLocked?: boolean;
  isFullAdmin?: boolean;
  onUpgradePlan?: () => void;
};

function DocumentResponseCell<T extends DocumentResponseCellRow>(
  props: Props<T>,
) {
  const {
    item,
    readingDurationByDocumentationId,
    onDocumentClick,
    isViewLocked,
    isFullAdmin,
    onUpgradePlan,
  } = props;
  const { t } = useTranslation();
  const itemOptions = item.options;
  const options = useMemo(
    () => (itemOptions || []) as FormTemplateQuestionFieldOption[],
    [itemOptions],
  );
  const resp = (item.answer?.resp || []) as FormAnswerResp[];

  const docIds = useMemo(() => {
    const ids = new Set<string>();
    options.forEach((opt) => {
      const id = opt.optionDocumentation?.documentationId;
      if (id) ids.add(id);
    });
    return Array.from(ids);
  }, [options]);

  const { documentsMap, loading } = useDocumentsMap(docIds);

  if (!docIds.length) {
    if (isViewLocked) {
      const lockedMessage = isFullAdmin ? (
        <SupportiveMessage
          color={SupportiveMessageColor.Yellow}
          text={t('advanced-documentation-features-not-in-plan')}
          actions={
            <Button
              buttonText={t('upgrade-plan')}
              buttonType={ButtonTypes.primaryFilled}
              onClick={onUpgradePlan}
            />
          }
          hasBottomSpace={false}
        />
      ) : (
        <SupportiveMessage
          color={SupportiveMessageColor.Gray}
          text={t('document-unavailable')}
          leftIconSymbolsOutlined
          hasBottomSpace={false}
        />
      );
      return (
        <div className={styles.supportiveMessageWrap}>{lockedMessage}</div>
      );
    }
    return <TextCell {...props} value="-" />;
  }

  if (loading) {
    return <CollapseCell {...props} value={<CircleLoader />} minHeight={120} />;
  }

  const respByOptionId = new Map<string, FormAnswerRespWithReadStatus>();
  resp.forEach((r) => {
    if (r.optionId)
      respByOptionId.set(r.optionId, r as FormAnswerRespWithReadStatus);
  });

  // BE removed `readingDurationSeconds` from FormAnswerResp — match
  // `resp.documentationVersionReadId` against the parent answer's
  // `documentationVersionReads` array to recover per-option read time.
  const readingDurationByDvrId = new Map<string, number>();
  (item.answer?.documentationVersionReads || []).forEach((dvr) => {
    if (dvr?.id && typeof dvr.readingDurationSeconds === 'number') {
      readingDurationByDvrId.set(dvr.id, dvr.readingDurationSeconds);
    }
  });

  const sorted = [...options].sort((a, b) => a.position - b.position);

  const list = (
    <div className={styles.documentList}>
      {sorted.map((opt) => {
        const docId = opt.optionDocumentation?.documentationId;
        if (!docId) return null;
        const optResp = respByOptionId.get(opt.id);
        const status = optResp?.status;
        const isRead = status === DocumentationVersionReadStatusEnum.Read;
        const isReadPreviously =
          status === DocumentationVersionReadStatusEnum.ReadPreviously;
        const isReadAny = isRead || isReadPreviously;
        let chipLabel = t('unread');
        if (isRead) chipLabel = t('read');
        else if (isReadPreviously) chipLabel = t('read-previously');
        const dvrId = optResp?.documentationVersionReadId;
        const directHit = dvrId ? readingDurationByDvrId.get(dvrId) : undefined;
        const fallbackHit =
          isReadAny && directHit === undefined
            ? readingDurationByDocumentationId?.get(docId)
            : undefined;
        const readingTime = formatReadingDuration(
          directHit ?? fallbackHit ?? undefined,
        );
        const doc = documentsMap.get(docId);
        return (
          <div key={opt.id} className={styles.documentRow}>
            <div className={styles.documentRowPreview}>
              {doc ? (
                <DocumentationPreview
                  attachment={doc.currentVersion?.attachmentAttached}
                  customName={doc.name}
                  view={DocumentationPreviewEnum.RowCard}
                  notValidated={
                    doc.validationStatus !==
                    DocumentationValidationStatusEnum.Validated
                  }
                  onClick={
                    onDocumentClick ? () => onDocumentClick(docId) : undefined
                  }
                />
              ) : (
                <SupportiveMessage
                  text={t('document-unavailable')}
                  leftIconSymbolsOutlined
                  color={SupportiveMessageColor.Gray}
                  hasBottomSpace={false}
                />
              )}
            </div>
            <div>
              <Chip
                type={isReadAny ? ChipTypes.chipGreen : ChipTypes.chipRed}
                label={chipLabel}
                size={ChipSizes.small}
              />
            </div>
            <div>
              <Typography variant="caption" className={styles.documentRowTime}>
                {readingTime}
              </Typography>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <CollapseCell
      {...props}
      value={list}
      minHeight={120}
      cropColor={CollapseCropColor.White}
    />
  );
}

export default DocumentResponseCell;
